import type { Dispatch } from 'react';

import { Stack, darken, getContrastRatio, lighten } from '@mui/material';

import { useApp } from '@system/app';

import {
  ButtonCollapsed,
  MenuExtended,
  SidebarFooterMenuProfile,
  SidebarFooterMenuStart,
} from './components';
import {
  SIDEBAR_FOOTER_MENU_ADMIN,
  SIDEBAR_FOOTER_MENU_END,
  SIDEBAR_FOOTER_MENU_SLOTS,
  SIDEBAR_FOOTER_MENU_USER,
  SIDEBAR_HEAD_MENU,
} from './constants';
import { GetSidebarBodyMenu } from './utils/utils';

type SidebarProps = {
  isCollapsed: boolean;
  setIsCollapsed: Dispatch<boolean>;
};

export const Sidebar = (props: SidebarProps) => {
  const { isCollapsed, setIsCollapsed } = props;
  const { isAdm } = useApp();
  const SIDEBAR_BODY_MENU = GetSidebarBodyMenu();

  const BottomMenuItems = SidebarFooterMenuStart()
    .concat(isAdm ? SIDEBAR_FOOTER_MENU_ADMIN : SIDEBAR_FOOTER_MENU_USER)
    .concat(SidebarFooterMenuProfile())
    .concat(SIDEBAR_FOOTER_MENU_END);

  return (
    <Stack
      data-testid="main-sidebar"
      sx={{
        position: 'sticky',
        marginTop: '30px',
        zIndex: theme => theme.zIndex.drawer + 2,
        color: t =>
          getContrastRatio(t.palette.primary.main, t.palette.background.paper) >
          1
            ? t.palette.aurora.dark
            : t.palette.common.white,
      }}
    >
      <ButtonCollapsed
        isCollapsed={isCollapsed}
        onCollapsed={setIsCollapsed}
      />
      <Stack
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            background: t => t.palette.primary.main,
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: t => lighten(t.palette.primary.main, 0.1),
          },
          '&::-webkit-scrollbar-thumb': {
            background: t => darken(t.palette.primary.main, 0.2),
            borderRadius: 2,
          },
          height: 'calc(100vh - 30px)',
          backgroundColor: t => t.palette.background.paper,
        }}
      >
        <Stack
          sx={{
            minWidth: isCollapsed ? '68px' : '300px',
            maxWidth: isCollapsed ? '68px' : '300px',
            position: 'relative',
            transition: '0.5s ease',
            transitionProperty: 'width, max-width, min-width',
            height: '100%',
          }}
        >
          <MenuExtended
            slots={{
              list: {
                sx: {
                  mt: -1,
                },
              },
            }}
            items={SIDEBAR_HEAD_MENU}
            isCollapsed={isCollapsed}
          />
          <MenuExtended
            items={SIDEBAR_BODY_MENU}
            isCollapsed={isCollapsed}
          />
          <MenuExtended
            items={BottomMenuItems}
            isCollapsed={isCollapsed}
            slots={SIDEBAR_FOOTER_MENU_SLOTS}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
